import React from 'react';
import { getAdminBadge } from '../utils/adminBadge';

const TOGGLES = [
  { key: 'favorite', label: 'Favorites', icon: '♥', help: 'Show only favorites', activeClass: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200' },
  { key: 'latest_visit', label: 'Latest visit', icon: '↻', help: 'Only the most recent visit of each store among the matching photos.', activeClass: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' },
  { key: 'deleted', label: 'Deleted (view)', icon: '🗑', help: 'Show only deleted', adminOnly: true, activeClass: 'border-gray-400 bg-gray-100 text-gray-800 dark:border-gray-500 dark:bg-gray-800 dark:text-gray-100' },
];

/**
 * The gallery's on/off views, where they are always in sight.
 *
 * They used to sit in the sidebar footer, below every dimension and the AI section, so on a
 * desktop they only appeared after scrolling — and an active one gave no sign of itself from the
 * gallery. A filled pill says it is on; the explanation is the tooltip, not a sentence on screen.
 *
 * « Latest visit » is always shown, because the search refinements alone left it unnoticed. Past
 * the number of photos the server agrees to rank, it stays visible but greyed out, and its tooltip
 * says why. It is never greyed out while on, so it can always be switched off.
 *
 * @param {{ filters: object, onToggle: Function, isAdmin: boolean, t: Function, unavailable?: Object<string, string|null> }} props
 *   `unavailable` maps a toggle to the reason it cannot be switched on right now
 */
const ViewToggles = ({ filters, onToggle, isAdmin, t, unavailable = {} }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {TOGGLES
      .filter(toggle => !toggle.adminOnly || isAdmin)
      .map((toggle) => {
      const active = filters[toggle.key] === true;
      const reason = active ? null : unavailable[toggle.key];
      return (
        <button
          key={toggle.key}
          type="button"
          aria-pressed={active}
          aria-disabled={reason ? true : undefined}
          title={reason || t(toggle.help)}
          onClick={() => { if (!reason) onToggle(toggle.key, !active); }}
          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${active
            ? toggle.activeClass
            : reason
              ? 'cursor-not-allowed border-gray-200 text-gray-400 dark:border-gray-800 dark:text-gray-600'
              : 'border-gray-200 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'}`}
        >
          {toggle.adminOnly && getAdminBadge()}
          <span aria-hidden="true">{toggle.icon}</span>
          <span className="hidden sm:inline">{t(toggle.label)}</span>
          <span className="sr-only sm:hidden">{t(toggle.label)}</span>
        </button>
      );
    })}
  </div>
);

export default ViewToggles;
